import { Fragment, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  manpowerApi, usersApi,
  type GapAnalysis, type GapAnalysisJob, type GapAnalysisWeek, type GapAnalysisMetric,
  type GapAnalysisPursuit,
} from '@/lib/apiClient';
import { invalidateManpowerConfigQueries } from '@/lib/queryClient';
import { weekLabel } from '@/modules/projects/lib/weekLabel';
import { isFabDivision } from '@/lib/schedule';
import { fmtDate, fmtDateTime, todayInputValue } from '@/lib/dates';
import { WeeklyChart, CHART_COLORS, type ChartSeries } from './gapCharts';
import TravelRosterView from './TravelRosterView';
import { TravelPayrollReportPanel } from './TravelPayrollReportPanel';
import ExportMenu from './ExportMenu';
import { exportGapXlsx } from './lib/manpowerExport';

/**
 * Gap Analysis (#396) — the Travel and Gap tabs merged into one screen.
 *
 * Top to bottom: the seven supply boxes, the leadership charts, the weekly
 * and per-job tables, and the roster tucked behind a toggle.
 *
 * ── The one thing to understand before changing anything here ───────────────
 * DEMAND IS MEASURED AND SUPPLY IS TYPED, and the screen has to keep saying
 * so. Everything "needed" is computed from the crew schedule. Everything
 * "available" is seven numbers the manpower lead maintains by hand, because Paycor
 * lags and a roster export is a snapshot while he knows first (decided
 * 2026-09-10). So the boxes carry his name and the time he last saved them,
 * and the roster below — still fully editable — feeds NOTHING on this page.
 *
 * ⚠️ A box he has never filled in shows "—" and its gap column shows "not
 * set", never 0. A missing supply figure is not a shortfall of the whole
 * demand, and the API sends null precisely so this cannot be fudged.
 */

const TH: React.CSSProperties = {
  background: 'var(--ew-dark)', color: 'var(--ew-white)', fontWeight: 600, fontSize: 11,
  padding: '8px 10px', textAlign: 'left', whiteSpace: 'nowrap',
  borderRight: '1px solid rgba(255,255,255,0.07)',
};
const TD: React.CSSProperties = {
  borderBottom: '1px solid var(--ew-n-100)', borderRight: '1px solid var(--ew-n-100)',
  padding: '7px 10px', fontSize: 12, verticalAlign: 'middle',
};

function gapColor(gap: number): string {
  return gap > 0 ? 'var(--ew-danger)' : gap === 0 ? 'var(--ew-n-500)' : 'var(--ew-green-700)';
}
/** A gap nobody can compute reads as unknown, never as zero. */
function gapText(gap: number | null): string {
  if (gap === null) return 'not set';
  return gap === 0 ? '—' : gap > 0 ? `+${gap}` : String(gap);
}
const num = (v: number | null) => (v === null ? '—' : String(v));

/**
 * What the Fabrication filter has taken out of the data — jobs and pursuits
 * counted separately, because they go missing from different places.
 *
 * ⚠️ IT DOES NOT TAKE `showSystac`, AND THAT IS THE POINT OF IT BEING A
 * FUNCTION. Whether the pursuits are switched ON governs what is drawn and
 * which rows a table lists; it does not govern what the filter has removed from
 * the figures. The workbook writes its three "incl. SYSTAC" columns on every
 * export, computed from the filtered weeks, so a Fabrication bid's forecast is
 * missing from an export that lists no pursuits at all — and a count gated on
 * the toggle reported nothing, which in a horizon with no Fabrication jobs
 * suppressed the disclosure entirely. Not taking the toggle is the only way to
 * make that ungateable rather than remembered. (Codex on #500.)
 *
 * `jobs` are rows the two job tables are short of. `pursuits` is demand the
 * weekly figures are short of. A caller states which one it means.
 */
export function fabCounts(
  data: Pick<GapAnalysis, 'travelJobs' | 'localJobs' | 'systacTravelJobs' | 'systacLocalJobs'> | undefined,
): { jobs: number; pursuits: number } {
  const fabOf = (rows: { division: { id: number; label: string | null } | null }[]) =>
    rows.filter((r) => isFabDivision(r)).length;
  return {
    jobs: data ? fabOf([...data.travelJobs, ...data.localJobs]) : 0,
    pursuits: data ? fabOf([...data.systacTravelJobs, ...data.systacLocalJobs]) : 0,
  };
}

/**
 * …and what the filter has therefore REMOVED, which is what a disclosure is
 * about.
 *
 * ⚠️ TWO FUNCTIONS, BECAUSE "HOW MANY EXIST" AND "HOW MANY WERE TAKEN OUT" ARE
 * DIFFERENT QUESTIONS — and collapsing them broke the label the first time this
 * was fixed: with the filter OFF nothing is excluded, so a single function
 * returned zero and the control beside it announced "No Fabrication jobs in
 * this horizon" over a table listing two. The label asks `fabCounts`, the
 * export asks this.
 */
export function fabExcludedBy(
  hideFab: boolean,
  data: Pick<GapAnalysis, 'travelJobs' | 'localJobs' | 'systacTravelJobs' | 'systacLocalJobs'> | undefined,
): { jobs: number; pursuits: number } {
  return hideFab ? fabCounts(data) : { jobs: 0, pursuits: 0 };
}

export default function GapAnalysisView() {
  const [weeks, setWeeks] = useState(8);
  const [showRoster, setShowRoster] = useState(false);
  /**
   * SYSTAC pursuits stacked onto the crew charts.
   *
   * ⚠️ OFF by default. Everything else on this tab is measured or typed; this
   * is the one hypothetical, and a hypothetical that renders by default stops
   * reading as one. The person who wants it asks for it.
   */
  const [showSystac, setShowSystac] = useState(false);
  /**
   * Fabrication out of EVERY figure on the tab — the charts, the weekly table
   * and the two job tables.
   *
   * ⚠️ ON by default, which is the unusual direction — hiding data unasked —
   * and it is what was asked for: Fabrication runs its own crew, so on this
   * tab they are somebody else's problem mixed into yours ("most of the time
   * we will not want them mixed together"). The toggle says plainly how many
   * rows it is holding back, so the default can never be mistaken for there
   * being no fab work.
   *
   * ⚠️ IT USED TO SCOPE THE TWO JOB TABLES ALONE, and that was the defect: the
   * bars above counted the shop while the rows below it did not, so a reader
   * compared a total against a list that could not add up to it, and nothing
   * on screen said why. Hence the move up to the header row, beside the horizon
   * — a control that changes everything cannot sit halfway down the page.
   *
   * The narrowed weekly figures come from the API as `weeksExcludingFab`; they
   * are NOT derived here, because every one of them is a peak of daily sums and
   * subtracting Fabrication's own weekly peak would report a day nobody works.
   */
  const [hideFab, setHideFab] = useState(true);

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });
  const canEdit = (me?.roles?.includes('Admin') ?? false) || (me?.permissions?.includes('manpower.edit') ?? false);
  // The payroll report is open to schedulers AND to the payroll permission
  // (travel.payroll_report, the HR role), which does not carry manpower.edit.
  const canSeePayroll = canEdit || (me?.permissions?.includes('travel.payroll_report') ?? false);
  const [showPayroll, setShowPayroll] = useState(false);

  // ⚠️ Both options stated, neither inherited. lib/queryClient.ts sets a
  // two-minute global staleTime, so omitting these would leave the view
  // showing pre-edit numbers after a crew publish (#474).
  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ['manpower', 'gap-analysis', weeks],
    queryFn: () => manpowerApi.gapAnalysis(weeks),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  /**
   * THE WEEKS THE SCREEN IS ABOUT — one filter, every figure above the job
   * tables. Both arrays cover the same weeks in the same order, so the horizon,
   * the week labels and the per-job columns are unaffected by which is picked.
   */
  const shownWeeks = (hideFab ? data?.weeksExcludingFab : data?.weeks) ?? [];
  const weekStarts = shownWeeks.map((w) => w.weekStart);
  const series = (vals: (number | null)[], label: string, color: string, kind: 'bar' | 'line'): ChartSeries =>
    ({ key: label, label, color, kind, values: vals });

  /**
   * The yellow segment: HOW MUCH HIGHER THE WEEK'S PEAK GOES if the pursuits
   * land. It is not the pursuits' own crew, and it is not labeled as if it
   * were — see the series name below.
   *
   * ⚠️ A difference of two peaks the API computed, never a figure of its own.
   * `…WithSystac` is the combined peak — booked crew and the bid forecast can
   * fall on different days, so it is not measured + potential — and the API
   * guarantees it is never below the measured figure. Taking the difference
   * here is what makes the stack's top a day that actually exists.
   *
   * ⚠️ AND THAT IS WHY IT CAN READ LOW AGAINST THE PURSUITS' OWN CREW. Codex
   * (on #498) put the case exactly: 100 booked on Monday, then 10 booked plus a
   * 100-person pursuit on Tuesday, gives a measured peak of 100, a combined
   * peak of 110, and a yellow band of 10 — while the pursuit itself asks for
   * 100 people on Tuesday. Both numbers are true; they answer different
   * questions.
   *
   * The alternative Codex offers — take the components from the combined-peak
   * day — was NOT taken, because it would make the blue bar 10 instead of 100
   * in that example. Blue is the measured peak on every chart on this tab, it
   * is what the gap is computed from and what the tables list, so repainting it
   * to suit the segment above it trades a labeling problem for a much worse
   * one: two charts disagreeing about the same measured week.
   *
   * What WAS taken is the other half of the finding — the band says in words
   * that it is the added peak, so nobody reads it as a pursuit headcount. And a
   * reader who wants the pursuits' own crew now has somewhere to read it: each
   * bid is a ROW in the job tables, carrying its own anticipated crew
   * unaggregated. In the example above the band reads 10 and the row reads 100,
   * both correctly, and the two sit on one screen where the difference can be
   * seen rather than guessed at.
   */
  const systacBand = (withSystac: number, measured: number) => Math.max(0, withSystac - measured);

  /**
   * A crew chart, whole: measured demand as bars, the potential stacked on top
   * when asked for, and the manpower lead's supply line.
   *
   * ⚠️ `scaleSeries` IS THE COMBINED FIGURE IN BOTH STATES, and that is the
   * point of this helper existing rather than three inline arrays. The y axis
   * is fitted once, to what the week would need if the pursuits landed, and
   * never refitted — so turning the box on adds yellow and moves nothing else.
   * Refitting the axis to the taller stack shrank every blue bar to make room,
   * which made the measured demand look SMALLER at the exact moment the
   * difference was supposed to look larger.
   *
   * ⚠️ AND IT IS THE **COMBINED** PEAK, NOT MEASURED + BAND SUMMED HERE. The
   * API already guarantees `…WithSystac` is a day that exists and is never
   * below the measured figure; it is exactly the height the stack reaches, so
   * one bar series carrying it is the whole scale. (Fitting the axis to the
   * measured series instead left the yellow running off the top of the plot.)
   *
   * The cost is a little empty chart above the bars while the pursuits are
   * hidden, which is the trade that was asked for: no bar is ever cut.
   */
  const crewChart = (
    measured: (number | null)[], combined: number[], label: string,
    available: (number | null)[], availableLabel: string,
  ) => {
    const demand = series(measured, label, CHART_COLORS.NEEDED, 'bar');
    const supply = series(available, availableLabel, CHART_COLORS.AVAILABLE, 'line');
    const band: ChartSeries[] = showSystac
      ? [{
          key: 'systac', label: 'Added peak if SYSTAC won', color: CHART_COLORS.POTENTIAL,
          edge: CHART_COLORS.POTENTIAL_EDGE, kind: 'bar' as const,
          values: combined.map((c, i) => systacBand(c, (measured[i] ?? 0))),
        }]
      : [];
    // The band sits between the two so it stacks on the blue and the legend
    // reads bottom-of-stack, top-of-stack, line.
    return {
      series: [demand, ...band, supply],
      scaleSeries: [series(combined, label, CHART_COLORS.NEEDED, 'bar'), supply],
      stackBars: showSystac,
    };
  };

  /**
   * The gap trend's three lines, at a given setting of the pursuits toggle.
   *
   * ⚠️ Taken from `weekFigures` — the SAME function the week-by-week table
   * reads — so the chart and the row beneath it cannot state different gaps for
   * one week. Whatever gets decided about gapping the potential is then decided
   * in one place instead of two.
   *
   * Parameterised rather than fixed to `showSystac`, because the y-axis needs
   * both readings at once: see `scaleSeries` at the call site.
   */
  const gapLines = (withSystac: boolean): ChartSeries[] => [
    series(shownWeeks.map((w) => weekFigures(w, withSystac).totalGap), 'Total gap', CHART_COLORS.NEEDED, 'line'),
    series(shownWeeks.map((w) => weekFigures(w, withSystac).travelerGap), 'Traveler gap', CHART_COLORS.THIRD, 'line'),
    series(shownWeeks.map((w) => weekFigures(w, withSystac).localGap), 'Local gap', CHART_COLORS.AVAILABLE, 'line'),
  ];

  /**
   * Rows for the tables, with Fabrication held back when the toggle says so.
   *
   * ⚠️ Generic over the row type so a PURSUIT goes through the same call. Both
   * carry `division` for exactly this reason — one `isFabDivision` decides what
   * the tables show and (through the API's matching rule) what the charts
   * count, and a second filter written for pursuits could only drift from it.
   */
  const visible = <T extends { division: { id: number; label: string | null } | null }>(rows: T[]) =>
    hideFab ? rows.filter((r) => !isFabDivision(r)) : rows;
  /** Pursuits reach the tables only when the toggle asks for them. */
  const shownPursuits = (rows: GapAnalysisPursuit[] | undefined) =>
    showSystac ? visible(rows ?? []) : [];
  /**
   * Fabrication, counted as TWO figures, because the toggle holds back two
   * different things and they do not move together.
   *
   * ⚠️ ONE INTEGER CANNOT CARRY BOTH, AND TRYING TO MAKE IT WAS A DEFECT. The
   * count started as "rows the screen is hiding", which is toggle-dependent: a
   * Fabrication pursuit is a row only while the pursuits are switched on. But
   * the WORKBOOK always writes its three "incl. SYSTAC" columns, and those are
   * computed from `shownWeeks` — so with the pursuits off, a Fabrication bid's
   * forecast was already out of the exported figures while the disclosure,
   * counting rows, said nothing about it. With no Fabrication JOBS in the
   * horizon the count reached zero, the Summary line was suppressed entirely,
   * and the file narrowed its own totals in silence. (Codex on #500.)
   *
   * So: `fabJobsHidden` is rows, `fabPursuitsHidden` is demand, and each
   * disclosure below takes the one it is actually about.
   */
  // ⚠️ The label describes what EXISTS, not what was excluded — it has to read
  // sensibly with the filter off, when nothing is excluded at all.
  const { jobs: fabJobsHidden, pursuits: fabPursuitsHidden } = fabCounts(data);

  /** Fabrication rows the screen could show — pursuits only while they are on. */
  const fabRowsHidden = fabJobsHidden + (showSystac ? fabPursuitsHidden : 0);
  /** …and how to say that, without calling a bid a job. */
  const fabRowsPhrase = () => {
    const jobs = `${fabJobsHidden} Fabrication job${fabJobsHidden === 1 ? '' : 's'}`;
    const bids = `${fabPursuitsHidden} Fabrication pursuit${fabPursuitsHidden === 1 ? '' : 's'}`;
    if (!showSystac || fabPursuitsHidden === 0) return jobs;
    if (fabJobsHidden === 0) return bids;
    return `${jobs} and ${fabPursuitsHidden} pursuit${fabPursuitsHidden === 1 ? '' : 's'}`;
  };

  return (
    <div>
      {/* Printed header — invisible on screen, where the live controls say all
          of this. Same shape as the crew schedule's, so the two sheets read as
          one family, and it also clears the "Horizon" label that was left
          stranded on paper once its select was hidden. */}
      <div className="mp-print-summary" aria-hidden>
        <div className="mp-print-title">Project Tracker — manpower gap analysis</div>
        <dl className="mp-print-meta">
          <dt>Horizon</dt>
          <dd>{weeks} weeks from the current week</dd>
          <dt>Demand</dt>
          <dd>Measured from the crew schedule</dd>
          <dt>Supply</dt>
          <dd>
            Maintained by hand, not calculated
            {data?.supply.updatedAt
              ? <> — last updated {fmtDateTime(data.supply.updatedAt)}</>
              : <> — never set</>}
          </dd>
          {/* ⚠️ The screen's own note about this is a control label, and the
              print rules strip controls — so on paper the job tables were short
              of rows with nothing anywhere saying why, while the charts above
              them still counted that crew. This is the same disclosure the crew
              schedule's printed header makes about its filters, for the same
              reason: a filtered sheet that does not admit it reads as the whole
              picture. (Codex on #498.) */}
          {/* Paper shows exactly what the screen shows, so this takes the
              screen's row count — the pursuits' own forecast is not on the
              page at all while their toggle is off. The workbook is the one
              that differs, and it discloses separately. */}
          {hideFab && fabRowsHidden > 0 && (
            <>
              <dt>Fabrication</dt>
              <dd>
                excluded — {fabRowsPhrase()} left out of the job tables and of every chart
                and weekly figure above them
              </dd>
            </>
          )}
          {/* The yellow band is a hypothetical, and on paper there is no
              checkbox to say it is switched on. Stated here, or a printed sheet
              of combined figures is indistinguishable from a printed sheet of
              measured ones. */}
          {showSystac && (
            <>
              <dt>SYSTAC pursuits</dt>
              <dd>
                included — crew figures are what the week would need if every pursuit in it
                were won and staffed to its bid
              </dd>
            </>
          )}
          <dt>Printed</dt>
          <dd>{fmtDate(todayInputValue())}</dd>
        </dl>
      </div>

      {/* One filter row, above everything it scopes. */}
      <div className="mp-noprint" style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12, color: 'var(--ew-n-500)' }}>
          Horizon{' '}
          <select
            aria-label="Weeks in the horizon"
            style={{ fontSize: 12, padding: '5px 8px', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)' }}
            value={weeks}
            onChange={(e) => setWeeks(Number(e.target.value))}
          >
            {[8, 13, 26].map((w) => <option key={w} value={w}>{w} weeks</option>)}
          </select>
        </label>
        {/* ⚠️ Beside the horizon because it scopes the same thing the horizon
            does — the WHOLE tab. It used to sit above the two job tables, back
            when it filtered only those, and that placement was the bug: the
            charts and the weekly table went on counting a shop the rows below
            them had dropped. A control that changes every figure on the page
            cannot be introduced halfway down it. */}
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: 'var(--ew-n-600)' }}>
          <input type="checkbox" checked={hideFab} onChange={(e) => setHideFab(e.target.checked)} />
          Hide Fabrication jobs
        </label>
        <span style={{ fontSize: 11.5, color: 'var(--ew-n-500)' }}>
          {fabRowsHidden === 0
            ? 'No Fabrication jobs in this horizon.'
            : hideFab
              ? `${fabRowsPhrase()} out of every figure below — Fabrication runs its own crew.`
              : `Counting ${fabRowsPhrase()} alongside the rest.`}
        </span>
        {isFetching && <span style={{ fontSize: 11, color: 'var(--ew-n-400)' }}>refreshing…</span>}

        {/* The workbook is built from `data` — exactly what is on screen, and
            nothing recomputed.

            ⚠️ Disabled while REFETCHING as well as while empty. Saving a supply
            box invalidates this very query, and react-query keeps serving the
            previous `data` until the refetch lands — so the window between the
            save being accepted and the new figures arriving is exactly when
            someone reaches for Export, and it would write the numbers they just
            changed away from. */}
        <span style={{ marginLeft: 'auto' }}>
          <ExportMenu
            what="the gap analysis"
            disabled={!data || isFetching}
            // ⚠️ WHAT IS ON SCREEN, not the raw payload — the filtered job
            // lists AND the filtered weekly figures. The Fabrication toggle is
            // on by default, so passing `data` straight through gave a workbook
            // holding jobs the screen says are hidden, to someone who never
            // touched a control. The weekly sheet now moves with it too: a
            // Summary saying Fabrication was held back, over totals that still
            // counted it, is the same contradiction one step removed.
            onExcel={() => {
              if (!data) return;
              exportGapXlsx(
                {
                  ...data,
                  weeks: shownWeeks,
                  travelJobs: visible(data.travelJobs),
                  localJobs: visible(data.localJobs),
                  // Empty unless the pursuits are switched on, so a workbook
                  // exported from the default screen holds only booked work.
                  systacTravelJobs: shownPursuits(data.systacTravelJobs),
                  systacLocalJobs: shownPursuits(data.systacLocalJobs),
                },
                weeks,
                // ⚠️ The PURSUIT count is NOT gated on `showSystac`, where the
                // screen's own label is. The workbook writes its three "incl.
                // SYSTAC" columns whatever that toggle says, and they come from
                // `shownWeeks` — so a Fabrication bid's forecast is out of the
                // exported figures even when no pursuit is listed anywhere in
                // the file. The disclosure has to describe the DATA, not the
                // screen. (Codex on #500.)
                { fabExcluded: fabExcludedBy(hideFab, data) },
              );
            }}
          />
        </span>
      </div>

      {isLoading && <p style={{ fontSize: 12, color: 'var(--ew-n-500)' }}>Loading the gap analysis…</p>}
      {isError && <p style={{ fontSize: 12, color: 'var(--ew-danger)' }}>Couldn't load the gap analysis.</p>}

      {/* Held at reduced opacity while refetching — no skeleton flash, no layout jump. */}
      {data && (
        <div style={{ opacity: isFetching ? 0.6 : 1, transition: 'opacity 120ms' }}>
          <SupplyBoxes
            metrics={data.supply.metrics}
            updatedAt={data.supply.updatedAt}
            canEdit={canEdit}
          />

          <section aria-label="Workforce outlook" style={{ marginTop: 18 }}>
            <h3 style={{ fontSize: 14, margin: '0 0 2px' }}>Workforce outlook</h3>
            <p style={{ fontSize: 11.5, color: 'var(--ew-n-500)', margin: '0 0 8px' }}>
              Bars are what the crew schedule asks for. The line is what field manpower says we have.
              {/* ⚠️ "how much higher the peak goes", NOT "what we would also
                  need" — the yellow is the increase in the week's peak, and
                  the pursuits' own crew can be larger than it when their busy
                  day is not the booked work's busy day. See systacBand. */}
              {/* ⚠️ Explains the empty space at the top of every chart, which
                  is there in BOTH states and would otherwise read as the
                  drawing being badly fitted. */}
              {showSystac
                ? <> Yellow is how much higher each week's peak would go if the SYSTAC pursuits landed.</>
                : <> The scale already allows for the SYSTAC pursuits, so turning them on adds the
                    yellow without moving anything else.</>}
            </p>

            {/* ⚠️ The wording is "if we won them", not "forecast". The figure is
                the estimator's bid peak spread flat across the bid duration,
                weekdays only — it is what the job ASKED for, not a prediction
                that we will get it, and not a curve. The three leadership and
                gap charts are deliberately untouched: a bid states crew and
                duration and says nothing whatever about leaders. */}
            <label className="mp-noprint" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: 'var(--ew-n-600)', marginBottom: 10 }}>
              <input type="checkbox" checked={showSystac} onChange={(e) => setShowSystac(e.target.checked)} />
              Include SYSTAC pursuits
              <span style={{ fontWeight: 400, color: 'var(--ew-n-500)' }}>
                — stacks how much higher each week's peak would go, from each pursuit's bid
              </span>
            </label>
            {/* ⚠️ THREE UP ON A 24" MONITOR, AND THAT NUMBER IS THE POINT.
                At a 420px floor this laid out FOUR columns on a 1920 screen
                (measured: 445px each), which squeezed each chart's 760-unit
                viewBox into ~415px — a 0.55× shrink that took the axis labels
                down to 5.5px. The charts were unreadable because they were too
                NUMEROUS per row, not because the type was small.

                520 is chosen against the arithmetic: at a ~1905px content
                width four columns would need 4×520 + 3×12 = 2116 and cannot
                fit, while three need 1584 and do. Six charts then land as two
                rows of three. Narrower screens step down to two, then one.

                ⚠️ This number no longer has to be kept in step with anything in
                gapCharts. It used to pair with a hard-coded VIEW_W of 600, and
                the pairing was already wrong: the track is ~598px at 1920, but
                the chart card's 16px padding and 1px border leave the svg only
                ~564px of it, so every chart carried a ~36px scrollbar the
                arithmetic here said it would not. WeeklyChart measures its own
                box now. What this floor still decides is how MANY charts share
                a row — which is the thing it was always really for.

                ⚠️ `min(520px, 100%)`, NOT a bare 520px. A bare minimum in
                minmax() is a hard floor: auto-fit drops to one column below
                520px but that column stays 520px wide, so the whole page
                scrolled sideways on a phone (measured: 69px at 375px, back
                when this was 420). The min() lets the track collapse to the
                container once there is less than 520px to give it. */}
            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(min(520px, 100%), 1fr))' }}>
              <WeeklyChart
                title="Total Manpower: Needed vs. Available"
                unit="Headcount" weekStarts={weekStarts}
                {...crewChart(
                  shownWeeks.map((w) => w.totalNeeded),
                  shownWeeks.map((w) => w.totalNeededWithSystac),
                  'Total needed',
                  shownWeeks.map((w) => w.totalAvailable), 'Total Field MP',
                )}
              />
              <WeeklyChart
                title="Traveling Workforce: Needed vs. On Roster"
                unit="Headcount" weekStarts={weekStarts}
                {...crewChart(
                  shownWeeks.map((w) => w.travelersNeeded),
                  shownWeeks.map((w) => w.travelersNeededWithSystac),
                  'Travelers needed',
                  shownWeeks.map((w) => w.travelersAvailable), 'FT travelers',
                )}
              />
              <WeeklyChart
                title="Traveling Leadership: Needed vs. Available"
                unit="Headcount" weekStarts={weekStarts}
                series={[
                  series(shownWeeks.map((w) => w.travelingLeadersNeeded), 'Leaders needed', CHART_COLORS.NEEDED, 'bar'),
                  series(shownWeeks.map((w) => w.travelingLeadersAvailable), 'FT traveler leaders', CHART_COLORS.AVAILABLE, 'line'),
                ]}
              />
              {/* ⚠️ The local pair is the travel pair with one input changed,
                  deliberately so. Same chart type, same two roles, same
                  colors, same order — a reader who has learned the traveling
                  charts has learned these. The ONLY honest difference is which
                  jobs and which boxes feed them. */}
              <WeeklyChart
                title="Local Workforce: Needed vs. Available"
                unit="Headcount" weekStarts={weekStarts}
                {...crewChart(
                  shownWeeks.map((w) => w.localCrewNeeded),
                  shownWeeks.map((w) => w.localCrewNeededWithSystac),
                  'Local crew needed',
                  shownWeeks.map((w) => w.localCrewAvailable), 'Local workforce',
                )}
              />
              <WeeklyChart
                title="Local Leadership: Needed vs. Available"
                unit="Headcount" weekStarts={weekStarts}
                series={[
                  series(shownWeeks.map((w) => w.localLeadersNeeded), 'Leaders needed', CHART_COLORS.NEEDED, 'bar'),
                  series(shownWeeks.map((w) => w.localLeadersAvailable), 'Local leaders', CHART_COLORS.AVAILABLE, 'line'),
                ]}
              />
              {/* ⚠️ THREE LINES, AND NO MORE. The fourth and fifth gaps exist
                  (leadership, travel and local) and are both in the table
                  below — but five lines crossing zero in one box is a plate of
                  spaghetti, and this chart's job is the shape of the shortfall,
                  not its every component. Total / Traveler / Local is the one
                  trio that partitions the company without overlapping: a
                  leadership line would double-count people already inside the
                  two crew lines. */}
              {/* ⚠️ AND THESE THREE FOLLOW THE SYSTAC TOGGLE, through the SAME
                  `weekFigures` the table below calls.

                  They used to read `w.totalGap` and friends straight off the
                  payload, which are measured-only — so with the pursuits on, a
                  week read +76 in the table and plotted +6 in the chart beside
                  it. Two surfaces disagreeing about one number is the failure
                  this whole tab is built against, and the table's own caption
                  claims it holds "the charts' own numbers written out", so the
                  screen was asserting the contradiction rather than merely
                  containing it. (Codex on #500.)

                  One function feeds both, so they cannot drift again: whatever
                  is decided about gapping the potential is decided once, in
                  weekFigures. */}
              <WeeklyChart
                title="Gap Trend — Total / Traveler / Local"
                // Says so in words, because a line chart cannot separate the
                // measured part from the potential the way a stacked bar does —
                // there is no yellow here to carry the distinction.
                unit={showSystac
                  ? 'Shortfall (people) — above zero is short · includes the SYSTAC pursuits'
                  : 'Shortfall (people) — above zero is short'}
                weekStarts={weekStarts} includeNegative
                series={gapLines(showSystac)}
                // ⚠️ BOTH SETS, and both are needed to cover the axis. A
                // with-SYSTAC gap is never below its measured one, so the
                // combined lines set the TOP and the measured lines set the
                // BOTTOM. Passing either alone would let the toggle move one
                // end of the scale — the same illusion the crew charts are
                // fitted once to avoid.
                scaleSeries={[...gapLines(false), ...gapLines(true)]}
              />
            </div>
          </section>

          <section aria-label="Week by week" style={{ marginTop: 20 }}>
            <h3 style={{ fontSize: 14, margin: '0 0 8px' }}>Week by week</h3>
            {/* ⚠️ The SAME two toggles as the charts, and that is the whole
                point of passing them down: this table is the charts' own
                numbers written out, so a reader who checks a bar against a row
                has to find the same figure. It did not move with either
                control before. */}
            <WeeksTable weeks={shownWeeks} showSystac={showSystac} />
          </section>

          <section aria-label="Travel jobs" style={{ marginTop: 22 }}>
            <h3 style={{ fontSize: 14, margin: '0 0 8px' }}>Travel jobs</h3>
            <JobsTable
              jobs={visible(data.travelJobs)}
              pursuits={shownPursuits(data.systacTravelJobs)}
              weekStarts={weekStarts}
              kind="travel"
            />
          </section>

          {/* Same table, other side of the same flag. Two lists rather than one
              with a Type column: the charts above are already split this way,
              and a reader scanning "who is short" wants the two populations
              apart, not interleaved and sorted by job number. */}
          <section aria-label="Local jobs" style={{ marginTop: 20 }}>
            <h3 style={{ fontSize: 14, margin: '0 0 8px' }}>Local jobs</h3>
            <JobsTable
              jobs={visible(data.localJobs)}
              pursuits={shownPursuits(data.systacLocalJobs)}
              weekStarts={weekStarts}
              kind="local"
            />
          </section>

          <section aria-label="Travel roster" style={{ marginTop: 24, borderTop: '1px solid var(--ew-n-100)', paddingTop: 14 }}>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ fontSize: 12 }}
              aria-expanded={showRoster}
              onClick={() => setShowRoster((v) => !v)}
            >
              {showRoster ? '▾ Hide travel roster' : '▸ Show travel roster'}
            </button>
            <span style={{ marginLeft: 10, fontSize: 11.5, color: 'var(--ew-n-500)' }}>
              Reference list of field staff and where they can be sent. Editable, and
              deliberately not a source for any figure above.
            </span>
            {showRoster && (
              <div style={{ marginTop: 14 }}>
                <TravelRosterView canEdit={canEdit} />
              </div>
            )}
          </section>

          {canSeePayroll && (
            <section aria-label="Travel payroll report" style={{ marginTop: 18, borderTop: '1px solid var(--ew-n-100)', paddingTop: 14 }}>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ fontSize: 12 }}
                aria-expanded={showPayroll}
                onClick={() => setShowPayroll((v) => !v)}
              >
                {showPayroll ? '▾ Hide travel payroll report' : '▸ Show travel payroll report'}
              </button>
              <span style={{ marginLeft: 10, fontSize: 11.5, color: 'var(--ew-n-500)' }}>
                Signed Travel Acknowledgments and their rates, for payroll.
              </span>
              {showPayroll && (
                <div style={{ marginTop: 14 }}>
                  <TravelPayrollReportPanel />
                </div>
              )}
            </section>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The manpower lead's seven boxes.
 *
 * Presented as stat tiles rather than a chart: each is one number, and a
 * one-bar bar chart would be a worse way to show it. Proportional figures, not
 * tabular — these are display-size numbers that do not align in a column.
 */
function SupplyBoxes({ metrics, updatedAt, canEdit }: {
  metrics: GapAnalysisMetric[];
  updatedAt: string | null;
  canEdit: boolean;
}) {
  const laborers = metrics.filter((m) => m.role === 'laborer');
  const leaders = metrics.filter((m) => m.role === 'leader');
  const total = metrics.find((m) => m.role === null);
  const sumOf = (ms: GapAnalysisMetric[]) =>
    ms.every((m) => m.value === null) ? null : ms.reduce((s, m) => s + (m.value ?? 0), 0);
  const breakdown = sumOf([...laborers, ...leaders]);

  // The manpower lead's own check: the six should account for everybody in the field.
  const mismatch =
    total?.value != null && breakdown !== null && breakdown !== total.value
      ? breakdown - total.value
      : null;

  return (
    <section aria-label="Field manpower — maintained by hand">
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        <h3 style={{ fontSize: 14, margin: 0 }}>Field manpower</h3>
        <span style={{ fontSize: 11.5, color: 'var(--ew-n-500)' }}>
          Maintained by hand, not calculated ·{' '}
          {updatedAt
            ? <>last updated <strong style={{ color: 'var(--ew-n-700)' }}>{fmtDateTime(updatedAt)}</strong></>
            : <span style={{ color: 'var(--ew-safety-700)' }}>never set</span>}
        </span>
      </div>

      <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        {total && <MetricBox metric={total} canEdit={canEdit} wide />}
        {laborers.map((m) => <MetricBox key={m.key} metric={m} canEdit={canEdit} />)}
        {leaders.map((m) => <MetricBox key={m.key} metric={m} canEdit={canEdit} />)}
      </div>

      {mismatch !== null && (
        <p role="note" style={{ fontSize: 11.5, color: 'var(--ew-safety-700)', margin: '8px 0 0' }}>
          The six category boxes add up to {breakdown}, which is {Math.abs(mismatch)}{' '}
          {mismatch > 0 ? 'more' : 'fewer'} than Total Field MP ({total?.value}). They are
          meant to account for everybody — worth a look, but nothing here is blocked.
        </p>
      )}
    </section>
  );
}

function MetricBox({ metric, canEdit, wide }: { metric: GapAnalysisMetric; canEdit: boolean; wide?: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const save = useMutation({
    mutationFn: (value: string) => manpowerApi.updateConfig(metric.key, value),
    onSuccess: () => {
      void invalidateManpowerConfigQueries(qc);
      setEditing(false);
    },
  });

  const parsed = draft.trim();
  const invalid = !/^\d{1,4}$/.test(parsed);

  return (
    <div style={{
      border: '1px solid var(--ew-n-100)', borderRadius: 'var(--ew-r-lg)',
      padding: '10px 12px', background: 'var(--ew-white, #fff)',
      gridColumn: wide ? 'span 2' : undefined, minWidth: 0,
    }}>
      <div style={{ fontSize: 11, color: 'var(--ew-n-500)', display: 'flex', alignItems: 'center', gap: 5 }}>
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{metric.label}</span>
        {metric.countsTowardGap && (
          <span title="Feeds the gap calculation" aria-label="Feeds the gap calculation"
                style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--ew-green)', flex: '0 0 auto' }} />
        )}
      </div>

      {editing ? (
        // ⚠️ WRAPS, and the input takes a line of its own. These tiles sit in a
        // `minmax(150px, 1fr)` grid, so the narrowest one leaves 126px of
        // content — and the controls want 179px (input 74 + Save 54 + ✕ 39 +
        // gaps). On one line the ✕ was rendered clean outside the tile border,
        // over the neighboring tile. `flex: 1 1 100%` forces the break so the
        // buttons share the second line (54 + 39 + 6 = 99, fits at 126), which
        // holds at every column width instead of only the wide ones.
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginTop: 6 }}>
          {/* ⚠️ The SAVED figure, for paper only.
              Printing while a box is open for editing hides the input and both
              buttons — they are controls — and there is no blur handler that
              commits or closes the edit, so the tile printed with no number at
              all. Every other figure on the sheet still reflects the persisted
              value, so a blank tile would be the one place the document
              contradicted itself. This shows what is stored, not the half-typed
              draft. (Codex on #488.) */}
          <span
            className="mp-printonly"
            style={{ font: 'var(--ew-fw-bold) 26px/1.1 var(--ew-font)', color: 'var(--ew-dark)' }}
          >
            {metric.value === null ? '—' : metric.value}
          </span>
          <input
            type="number" min={0} max={9999} autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !invalid) save.mutate(parsed);
              if (e.key === 'Escape') setEditing(false);
            }}
            style={{ flex: '1 1 100%', minWidth: 0, fontSize: 15, padding: '3px 6px', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)' }}
          />
          <button className="btn btn-primary" style={{ fontSize: 11, flex: '0 0 auto' }} disabled={invalid || save.isPending}
                  onClick={() => save.mutate(parsed)}>
            {save.isPending ? '…' : 'Save'}
          </button>
          <button className="btn btn-secondary" style={{ fontSize: 11, flex: '0 0 auto' }} onClick={() => setEditing(false)}>✕</button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 2 }}>
          {/* Proportional figures on a display-size number — tabular digits
              make a standalone value look loose. */}
          <span style={{ font: 'var(--ew-fw-bold) 26px/1.1 var(--ew-font)', color: metric.value === null ? 'var(--ew-n-300)' : 'var(--ew-dark)' }}>
            {metric.value === null ? '—' : metric.value}
          </span>
          {canEdit && (
            <button className="btn btn-secondary" style={{ fontSize: 10.5, padding: '2px 7px' }}
                    onClick={() => { setDraft(metric.value === null ? '' : String(metric.value)); save.reset(); setEditing(true); }}>
              {metric.value === null ? 'Set' : 'Edit'}
            </button>
          )}
        </div>
      )}
      {save.isError && <div style={{ fontSize: 10.5, color: 'var(--ew-danger)', marginTop: 3 }}>Couldn't save.</div>}
    </div>
  );
}

/**
 * The three crew triples a week-by-week row actually shows, and the gaps that
 * go with them.
 *
 * ⚠️ THE GAPS ARE RECOMPUTED HERE rather than read off the payload, and that is
 * the deliberate part. The API computes no gap against the potential, on the
 * sound argument that a shortfall against work nobody has been awarded can
 * start a hire for a job that never lands — and that argument still governs
 * what is stored and what the default screen says. But once a reader has asked
 * to see the pursuits, a row showing combined demand beside a gap computed from
 * measured demand alone is three numbers that do not subtract. Two honest
 * figures contradicting each other on one line is the worse failure.
 *
 * ⚠️ `null` STILL MEANS "the manpower lead has not typed that box", exactly as it does
 * everywhere else on this tab. An unset supply figure has no gap whatever the
 * demand is — never a shortfall the size of the whole demand.
 *
 * Exported for its own test: every value here is one somebody staffs against,
 * and a wrong one looks exactly like a right one.
 */
export function weekFigures(w: GapAnalysisWeek, showSystac: boolean) {
  const gapOf = (needed: number, available: number | null) =>
    available === null ? null : needed - available;
  if (!showSystac) {
    return {
      total: w.totalNeeded, totalGap: w.totalGap,
      travelers: w.travelersNeeded, travelerGap: w.travelerGap,
      local: w.localCrewNeeded, localGap: w.localCrewGap,
      share: w.travelerShareOfDemand,
    };
  }
  return {
    total: w.totalNeededWithSystac, totalGap: gapOf(w.totalNeededWithSystac, w.totalAvailable),
    travelers: w.travelersNeededWithSystac, travelerGap: gapOf(w.travelersNeededWithSystac, w.travelersAvailable),
    local: w.localCrewNeededWithSystac, localGap: gapOf(w.localCrewNeededWithSystac, w.localCrewAvailable),
    // Recomputed from the two figures ON THIS ROW, so a reader who divides them
    // by hand gets the printed number — which is what the measured share has
    // always been. Zero demand is 0%, never NaN.
    share: w.totalNeededWithSystac > 0 ? w.travelersNeededWithSystac / w.totalNeededWithSystac : 0,
  };
}

/**
 * Every figure on the page, by week.
 *
 * ⚠️ TWO HEADER TIERS, because there are now five needed/available/gap triples
 * and a flat row of nineteen labels is unreadable — "Leaders available" twice
 * over, forty characters apart, with nothing saying which is which. The group
 * row carries the noun and the cells carry only the role, so the repetition
 * becomes the structure instead of an ambiguity.
 *
 * ⚠️ IT FOLLOWS THE SYSTAC TOGGLE, and the GAPS FOLLOW IT TOO — see
 * `weekFigures` for why the gap is recomputed rather than read off the payload.
 * The caption below says in words what the crew columns hold once it is on.
 * (from review: "make it all update when we click and unclick that box.")
 *
 * ⚠️ THE TWO LEADER TRIPLES DO NOT MOVE. A bid states crew and duration and
 * says nothing whatever about leaders — the same reason the two leadership
 * charts have never carried a yellow band. Inventing a leader count for a
 * pursuit would be the one figure on this tab that came from nowhere.
 */
function WeeksTable({ weeks, showSystac }: { weeks: GapAnalysisWeek[]; showSystac: boolean }) {
  const GROUP_TH: React.CSSProperties = {
    ...TH, textAlign: 'center', fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase',
    borderBottom: '1px solid rgba(255,255,255,0.15)',
  };
  const RTH: React.CSSProperties = { ...TH, textAlign: 'right' };
  /**
   * A group heading, marked when its crew figures now carry the pursuits.
   *
   * The mark is in the header rather than on every cell because it is a
   * property of the COLUMN, not of a value — and a symbol repeated down 26 rows
   * stops being read after the second one.
   */
  const triple = (label: string, withSystac = false) => (
    <th style={GROUP_TH} colSpan={3} scope="colgroup">
      {label}
      {withSystac && (
        <span style={{ display: 'block', fontSize: 9, letterSpacing: 0, textTransform: 'none', color: 'var(--ew-safety)' }}>
          incl. SYSTAC
        </span>
      )}
    </th>
  );

  return (
    <div className="mp-scroll" style={{ overflowX: 'auto' }}>
      <table className="mp-fit" style={{ borderCollapse: 'collapse', width: '100%', minWidth: 1560 }}>
        <thead>
          <tr>
            <th style={{ ...TH, verticalAlign: 'bottom' }} rowSpan={2} scope="col">Week of</th>
            {triple('Total', showSystac)}
            {triple('Travelers', showSystac)}
            {triple('Traveling leaders')}
            {triple('Local crew', showSystac)}
            {triple('Local leaders')}
            <th style={GROUP_TH} colSpan={2} scope="colgroup">Active jobs</th>
            <th style={{ ...RTH, verticalAlign: 'bottom' }} rowSpan={2} scope="col">Traveler share</th>
          </tr>
          <tr>
            {['Total', 'Travelers', 'Traveling leaders', 'Local crew', 'Local leaders'].map((g) => (
              <Fragment key={g}>
                <th style={RTH} scope="col">Needed</th>
                <th style={RTH} scope="col">Available</th>
                <th style={RTH} scope="col">Gap</th>
              </Fragment>
            ))}
            <th style={RTH} scope="col">Travel</th>
            <th style={RTH} scope="col">Local</th>
          </tr>
        </thead>
        <tbody>
          {weeks.map((w) => {
            const cell = (v: number | null) => <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{num(v)}</td>;
            const gapCell = (g: number | null) => (
              <td style={{ ...TD, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                           color: g === null ? 'var(--ew-n-400)' : gapColor(g) }}>
                {gapText(g)}
              </td>
            );
            const s = weekFigures(w, showSystac);
            return (
              <tr key={w.weekStart}>
                <td style={{ ...TD, fontWeight: 600 }}>{weekLabel(w.weekStart)}</td>
                {cell(s.total)}{cell(w.totalAvailable)}{gapCell(s.totalGap)}
                {cell(s.travelers)}{cell(w.travelersAvailable)}{gapCell(s.travelerGap)}
                {cell(w.travelingLeadersNeeded)}{cell(w.travelingLeadersAvailable)}{gapCell(w.travelingLeaderGap)}
                {cell(s.local)}{cell(w.localCrewAvailable)}{gapCell(s.localGap)}
                {cell(w.localLeadersNeeded)}{cell(w.localLeadersAvailable)}{gapCell(w.localLeaderGap)}
                {cell(w.activeTravelJobs)}{cell(w.activeLocalJobs)}
                <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {Math.round(s.share * 100)}%
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {/* ⚠️ Total is NOT travelers + local. Each of the three is its own busiest
          day, and the company's busiest day need not be the travelers'. Stated
          here because a reader who adds the columns and gets a different number
          will otherwise assume one of them is wrong. */}
      <p style={{ fontSize: 11, color: 'var(--ew-n-500)', margin: '6px 0 0' }}>
        Each column is that week's peak day, taken on its own. Travelers and Local crew
        will not always add up to Total — the busiest day for the company need not be the
        busiest day for either group.
      </p>
      {/* ⚠️ Says what changed AND what did not. Three of the five triples moved
          and two did not, and a reader who spots the marked headers will
          reasonably wonder about the leaders. */}
      {showSystac && (
        <p style={{ fontSize: 11, color: 'var(--ew-n-500)', margin: '4px 0 0' }}>
          The three crew rows — Total, Travelers and Local crew — are the combined peak
          with the SYSTAC pursuits included, and their gaps are measured against it.
          Nothing has been awarded: this is what the week would need if every pursuit in
          it were won and staffed to its bid. The leader columns are unchanged, because a
          bid states crew and duration and says nothing about leaders.
        </p>
      )}
    </div>
  );
}

function JobsTable({ jobs, pursuits, weekStarts, kind }: {
  jobs: GapAnalysisJob[];
  /**
   * The SYSTAC pursuits for this side, or empty when the toggle is off.
   *
   * ⚠️ A SECTION OF THEIR OWN INSIDE THE TABLE, never interleaved with the
   * jobs above them. Every row above is work that is booked; these are bids on
   * work nobody has been awarded, and the two sorted together by job number
   * would put a hypothetical between two facts with only a color to tell them
   * apart. The band row that introduces them is what makes the distinction
   * survive a black-and-white print.
   */
  pursuits: GapAnalysisPursuit[];
  weekStarts: string[];
  /** Wording only — the two tables are otherwise identical, which is the point. */
  kind: 'travel' | 'local';
}) {
  if (!jobs.length && !pursuits.length) {
    return (
      <p style={{ fontSize: 12, color: 'var(--ew-n-500)' }}>
        No {kind} jobs have crew scheduled in this horizon.
      </p>
    );
  }
  /** Every column, for the band row that introduces the pursuits. */
  const SPAN = 4 + weekStarts.length;
  return (
    <div className="mp-scroll" style={{ overflowX: 'auto' }}>
      <table className="mp-fit" style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
        <thead>
          <tr>
            <th style={TH}>Job</th>
            <th style={TH}>State</th>
            <th style={{ ...TH, textAlign: 'right' }}>Leaders needed</th>
            <th style={TH}>Leaders named</th>
            {weekStarts.map((ws) => <th key={ws} style={{ ...TH, textAlign: 'right' }}>{weekLabel(ws)}</th>)}
          </tr>
        </thead>
        <tbody>
          {jobs.map((j) => (
            <tr key={j.projectId}>
              <td style={{ ...TD, fontWeight: 600 }}>{j.jobNumber ?? '—'} {j.title ? `· ${j.title}` : ''}</td>
              <td style={TD}>{j.state ?? '—'}</td>
              <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {j.leadersNeeded}
                {j.isLeadersOverride && (
                  <span title="Overridden on the project Manpower tab" style={{ marginLeft: 5, fontSize: 10, color: 'var(--ew-n-500)' }}>*</span>
                )}
              </td>
              <td style={TD}>
                {j.leaders.length
                  ? j.leaders.map((l) => (l.upcoming ? `${l.name} (upcoming)` : l.name)).join(', ')
                  : <span style={{ color: 'var(--ew-safety-700)' }}>unstaffed</span>}
              </td>
              {j.weeklyCrew.map((wc) => (
                <td key={wc.weekStart} style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                                                color: wc.peak > 0 ? 'var(--ew-dark)' : 'var(--ew-n-400)' }}>
                  {wc.peak || '—'}
                </td>
              ))}
            </tr>
          ))}

          {/* ⚠️ The pursuits, under a band that says what they are. Yellow
              carries them, the same yellow as the charts' band — but it never
              carries them ALONE: the heading row states it in words and the
              two leader cells read "n/a" rather than being merely empty, so
              the section survives grayscale and a reader who cannot separate
              the tint from white. */}
          {pursuits.length > 0 && (
            <>
              <tr>
                <td colSpan={SPAN} style={{
                  ...TD, background: 'var(--ew-safety-50)', borderTop: '2px solid var(--ew-safety-700)',
                  fontSize: 11.5, fontWeight: 600, color: 'var(--ew-safety-700)',
                }}>
                  SYSTAC pursuits — not awarded
                  <span style={{ fontWeight: 400, color: 'var(--ew-n-600)' }}>
                    {' '}· each row is that bid's own anticipated crew, spread flat across its
                    duration on weekdays. This is not the charts' yellow band, which is how much
                    higher the whole week's peak would go — the two answer different questions and
                    will not always match.
                  </span>
                </td>
              </tr>
              {pursuits.map((p) => (
                <tr key={`systac-${p.projectId}`} style={{ background: 'var(--ew-safety-50)' }}>
                  <td style={{ ...TD, fontWeight: 600 }}>{p.jobNumber ?? '—'} {p.title ? `· ${p.title}` : ''}</td>
                  <td style={TD}>{p.state ?? '—'}</td>
                  {/* ⚠️ "n/a", not a number and not a blank. A bid states crew
                      and duration and says nothing about leaders, so there is
                      no honest figure here — and an empty cell reads as one
                      nobody has filled in yet, which would put this row's
                      leaders into the unstaffed count in a reader's head. */}
                  <td style={{ ...TD, textAlign: 'right', color: 'var(--ew-n-400)' }} title="A bid states crew and duration, never leaders">n/a</td>
                  <td style={{ ...TD, color: 'var(--ew-n-400)' }}>n/a</td>
                  {p.weeklyCrew.map((wc) => (
                    <td key={wc.weekStart} style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                                                    color: wc.peak > 0 ? 'var(--ew-safety-700)' : 'var(--ew-n-400)' }}>
                      {wc.peak || '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>
      {pursuits.length > 0 && (
        <p style={{ fontSize: 11, color: 'var(--ew-n-500)', margin: '6px 0 0' }}>
          A pursuit's weekly figure is its bid peak, not a total — the bid gives a peak crew and
          a duration, not a curve. Days it already has crew booked on are left to the scheduled
          job above, so no day is counted twice.
        </p>
      )}
    </div>
  );
}
